import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Checkbox, Container, Grid, Button, Header, Divider, Form, Menu, Message, Modal } from 'semantic-ui-react';
import { FormSongVersion } from '../forms/FormSongVersion';

const defaultCloning = false;

const emptyCycleVersion = {
  alternate_title: "",
  commission: "",
  composition_end_date: "1900-01-01T00:00:00+00:00",
  composition_start_date: "1900-01-01T00:00:00+00:00",
  dedication: "",
  id: 0,
  notes: "",
  premiere_date: "1900-01-01T00:00:00+00:00",
  song_cycle_id: 0,
  title: "",
  version_name: "default",
  song_cycle_version: {},
  version_assoc: [],
  song_versions: [], 
  is_deleted: null
};

const emptyCycle = {
  id: 0,
  song_cycle_id: 0,
  song_cycle_version: emptyCycleVersion,
  song_versions: [],
  version_assoc: []
};

export const EditSongCycleVersion = ({songOptions, openButton, onSuccess, version = emptyCycle, cloning = defaultCloning, cycleId = 0, composerId = 0 }) => {
  
  const [ modalOpen, setModalOpen ] = useState(false);
  const [ creating, setCreating ] = useState();
  const [ data, setData ] = useState({});
  const [ versionAssoc, setVersionAssoc ] = useState([]);
  const [ versionIds, setVersionIds ] = useState([]);
  const [ header, setHeader ] = useState('');
  const [ spinnerActive, setSpinnerActive ] = useState(false);
  const [ loadingMessage, setLoadingMessage ] = useState("");
  
  useEffect(() => {
    console.log("version being edited x", version);
    console.log("song options", songOptions);

    if (version === null) {
      setData(emptyCycleVersion);
    } else {
      setData(JSON.parse(JSON.stringify(version.song_cycle_version)));
      const unsorted = JSON.parse(JSON.stringify(version.version_assoc));
      setVersionAssoc(unsorted.sort((a,b) => a.sequence - b.sequence));

      setVersionIds(version.song_versions.map(version => version.id));
    }

    const isCreating = version === emptyCycle || cloning;
    setCreating(isCreating);
    let headertext = isCreating ? "Create new song cycle version" : "Edit song cycle version";
    setHeader(<Header as='h3' color='purple' className='subtitle'>{headertext}</Header>);
  }, [version, cloning, songOptions]);

  
  if (!data === undefined || !songOptions) {
    return <div/>;
  }

  const determineNecessaryAssocUpdates = (scvId) => {
    const result = [];
    versionIds.forEach((id, i) => {
      if (i >= versionAssoc.length) {
        const message = {
          ID: 0,
          Sequence: i,
          SongCycleVersionId: scvId,
          SongVersionId: id,
          CreatedAt: null,
          DeletedAt: null,
          UpdatedAt: null
        };
        result.push({
          method: "POST",
          body: message,
          headers: {
            "Content-type": "application/json"
          }
        });
      } else if (versionAssoc[i].song_version_id !== id) {
        const message = {
          ID: versionAssoc[i].id,
          Sequence: i,
          SongCycleVersionId: scvId,
          SongVersionId: id,
          CreatedAt: null,
          DeletedAt: null,
          UpdatedAt: null
        };
        result.push({
          method: "PUT",
          body: message,
          headers: {
            "Content-type": "application/json"
          }
        });
      }
    });

    return result;
  }

  const submit = async () => {
    console.log("to submit: data", data);
    console.log("to submit: versIds", versionIds);
    console.log("version_assoc", versionAssoc);

    setSpinnerActive(true);

    let newCycleId = 0;

    // create cycle if needed
    if (cycleId === 0) {
      const newCycleResp = await fetch(`/go/api/v1/song-cycles`, {
        method: "POST",
        body: JSON.stringify({ComposerID: composerId, ID: 0}),
        headers: {
          "Content-type": "application/json"
        }
      });
      if (newCycleResp.status !== 200) {
        console.log("problem registering new cycle record");
      }

      const newCycleJson = await newCycleResp.json();

      newCycleId = newCycleJson.ID;
    }

    setLoadingMessage("Submitting song cycle version...");

    const cycleVersionId = creating ? 0 : data.id;

    const message = {
      ID: cycleVersionId,
      AlternateTitle: data.alternate_title,
      Commission: data.commission,
      CompositionEndDate: data.composition_end_date,
      CompositionStartDate: data.composition_start_date,
      Dedication: data.dedication,
      Notes: data.notes,
      PremiereDate: data.premiere_date,
      SongCycleId: cycleId === 0 ? newCycleId : cycleId,
      Title: data.title,
      VersionName: data.version_name,
      IsDeleted: data.is_deleted
    };

    const method = creating ? "POST" : "PUT";
    const messageUrl = `/go/api/v1/song-cycle-versions${creating ? "" : `/${message.ID}`}`;

    const sendCycleVersionResp = await fetch(messageUrl, {
      method: method,
      body: JSON.stringify(message),
      headers: {
        "Content-type": "application/json"
      }
    });

    setSpinnerActive(false);

    let newCycleVersionId = 0;

    if (sendCycleVersionResp.status === 200) {
      const scvJson = await sendCycleVersionResp.json();
      newCycleVersionId = scvJson.ID;
    } else {
      setLoadingMessage("Error saving version");
    }

    const assocUpdates = determineNecessaryAssocUpdates(cycleVersionId === 0 ? newCycleVersionId : cycleVersionId);

    console.log("assocUpdates", assocUpdates);

    let anyFailures = false;

    if (assocUpdates.length > 0) {
      setLoadingMessage("Registering associations...");
      for(const update of assocUpdates) {
        console.log("updating assoc", update);
        const messageUrl = `/go/api/v1/song-version-entry-in-cycles${update.method == "POST" ? "" : `/${update.body.ID}`}`;
        update.body = JSON.stringify(update.body);
        const sendUpdateResp = await fetch(messageUrl, update);
        if (sendUpdateResp.status !== 200) {
          anyFailures = true;
        }
      }
    }

    if (versionAssoc.length > versionIds.length && !creating) {
      setLoadingMessage("Deleting removed associations...");
      for (const assoc of versionAssoc.slice(versionIds.length)) {
        const messageUrl = `/go/api/v1/song-version-entry-in-cycles/${assoc.id}`;
        const sendDeleteResp = await fetch(messageUrl, {
          method: "DELETE"
        });
        if (sendDeleteResp.status !== 200) {
          anyFailures = true;
        }
      }
    }

    if (anyFailures) {
      setLoadingMessage("failure updating associations");
    } else {
      setLoadingMessage("");
      setModalOpen(false);
    }
  };

  const ddOptions = songOptions.flatMap(song => {
    return song.versions.map(version => {
      return {
        value: version.id,
        text: `${version.version_info.title} -- Version: ${version.version_info.version_name}`,
        key: version.id
      }
    });
  });

  const handleSelect = (i) => {
    return (e, {value}) => {
      setVersionIds(old => {
        const newval = JSON.parse(JSON.stringify(old));
        newval[i] = value;
        return newval;
      });
    };
  };

  const handleRemove = (i) => {
    return () => {
      setVersionIds(old => {
        const newval = JSON.parse(JSON.stringify(old));
        newval.splice(i, 1);
        return newval;
      });
    };
  };

  const handleAdd = () => {
    setVersionIds(old => {
      const newval = JSON.parse(JSON.stringify(old));
      newval.push(songOptions[0].versions[0].id);
      return newval;
    })
  }

  const dropdowns = versionIds.map((id, i) => {
    return ( <div key={i} className='edit-cycle-songversion-selector-row'>
      <Header as='h4' color='purple' className='subtitle'>{i+1}:</Header>
      <Form.Dropdown selection options={ddOptions} value={id} onChange={handleSelect(i)}/>
      <Button onClick={handleRemove(i)}>Remove</Button>
    </div>)
  });

  const inputHandler = (e, {name, value}) => {
    console.log("inputHandler", name, value);
    setData((old) => {
      const newData = JSON.parse(JSON.stringify(old));
      newData[name] = value;
      return newData;
    });
  };

  const infoForm = (
    <Form>
      <Header as='h4' color='purple' className='subtitle'>Included Song Versions</Header>
      {dropdowns}
      <Button onClick={handleAdd}>Add</Button>
      <Header as='h4' color='purple' className='subtitle'>Song Cycle Version Info</Header>
      <Form.Group widths='equal'>
        <Form.Input fluid name="version_name" label="Name of Version" value={data.version_name} placeholder='Name of Version'
        onChange={inputHandler}/>
        <Form.Input fluid name="title" label="Cycle Title" value={data.title}
        onChange={inputHandler}/>
      </Form.Group>
      <Form.Group widths='equal'>
        <Form.Input fluid name="composition_start_date" label="Composition Start Date" value={data.composition_start_date} />
        <Form.Input fluid name="composition_end_date" label="Composition End Date" value={data.composition_end_date} />
      </Form.Group>
      <Form.Input fluid name="notes" label="Notes" value={data.notes} />
    </Form>
  );

  return (
    <Modal size='large' trigger={openButton} open={modalOpen} onClose={() => setModalOpen(false)}
    onOpen={() => setModalOpen(true)}>
      <Modal.Header>
        {header}
      </Modal.Header>
      <Modal.Content scrolling>
        {infoForm}
      </Modal.Content>
      <Modal.Actions>
        <span>{loadingMessage}</span>
        <Button color='red' onClick={() => {setModalOpen(false);}} disabled={spinnerActive}>Discard</Button>
        <Button color='green' onClick={submit} loading={spinnerActive}>Save Changes</Button>
      </Modal.Actions>
    </Modal>
  );
};
